<template>
  <div class="abonnement">
    <div class="ods-grid ods-grid--gap ods-padding-left-6">
      <div class="abonnement__content ods-grid__column--12 ods-grid__column--7-breakpoint-medium">
        <h3
          v-if="isExternalLink(abonnement.url)"
          :href="abonnement.url"
          target="_blank"
          rel="noopener noreferrer"
          class="ods-text--size-kilo ods-text--weight-medium ods-margin-bottom-2 ods-text--size-juliett-breakpoint-medium"
        >
          {{ abonnement.tittel }}
        </h3>
        <router-link v-else :to="{ path: abonnement.url }">
          {{ abonnement.tittel }}
        </router-link>
        <p class="mb-size-0 ods-margin-bottom-0">{{ abonnement.ingress }}</p>
        <p v-if="abonnementIsInEditMode !== abonnement.eksternId">
          {{ $t('abonnementer.email.other') }} {{ varselSendesTil }}
        </p>
      </div>
      <div
        v-if="abonnementIsInEditMode !== abonnement.eksternId"
        class="ods-grid__column--12 ods-grid__column--5-breakpoint-medium ods-padding-left-25-breakpoint-medium"
      >
        <button
          type="button"
          class="abo-knapp ods-flex ods-flex-direction-row ods-text--size-kilo ods-text--weight-light ods-color-bg-white ods-color-text-blue-dark ods-text--align-left ods-flex-align-items-center"
          @click="showModal(abonnement)"
        >
          <svg class="abo-knapp__icon ods-block" viewBox="0 0 32 32" aria-hidden="true">
            <use href="#close"></use>
          </svg>
          <span class="abo-knapp__text ods-text--size-juliett-breakpoint-medium">{{
            $t('abonnementer.delete.title')
          }}</span>
        </button>
        <ok-modal v-show="isModalVisible" @close="closeModal" @confirm="stopAbonnement">
          <template #headerText>
            {{ $t('abonnementer.delete.title') }}
          </template>
          <template #body>
            {{ $t('abonnementer.delete.confirm') }}
          </template>
        </ok-modal>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import OkModal from '@/components/OkModal.vue';

export default {
  name: 'AktiveAbonnementer',

  components: {
    OkModal,
  },

  props: {
    abonnement: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    abonnementIsInEditMode: undefined,
    abonnementUpdateTriggered: false,
    selectedEpost: undefined,
    isModalVisible: false,
  }),

  computed: {
    ...mapState(['brukerinfo']),
    ...mapState('representasjoner', ['currentRepresentation']),
    ...mapGetters('abonnementer', ['abonnementerUtenomEidAdresse']),

    varselSendesTil() {
      const brukerEpost = this.brukerinfo.email
        ? this.$t('abonnementer.email.idPorten', { epost: this.brukerinfo.email })
        : this.$t('abonnementer.email.idPortenMissing');
      const abonnementFraVuex = this.abonnementerUtenomEidAdresse.find(
        (ab) => ab.eksternId === this.abonnement.eksternId && ab.abonnementstypeId === this.abonnement.abonnementstypeId
      );

      return abonnementFraVuex && abonnementFraVuex.annenEpost ? abonnementFraVuex.annenEpost : brukerEpost;
    },
  },

  methods: {
    ...mapActions(['addGlobalMessage']),
    ...mapActions('abonnementer', ['stoppAbonnement']),

    isExternalLink(url) {
      return url.slice(0, 4) === 'http';
    },

    stopAbonnement() {
      this.closeModal();
      this.stoppAbonnement(this.abonnement);
      this.addGlobalMessage({
        messageType: 'info',
        message: this.$t('abonnementer.delete.stop'),
      });
    },

    showModal() {
      this.abonnementUpdateTriggered = true;
      this.isModalVisible = true;
    },

    closeModal() {
      this.abonnementUpdateTriggered = false;
      this.isModalVisible = false;
    },
  },
};
</script>

<style lang="scss">
@use 'sass:map';
@use 'sass:selector';
@use '~@ods/system/colors' as *;

.abonnement {
  border: 2px solid $gray;
  padding: 1.5rem 0;

  &:not(:last-child) {
    margin-bottom: 2rem;
  }

  $-button-name: 'abo-knapp';

  .#{$-button-name} {
    $btn-link-txt: $blue-dark;
    $btn-focus-bg: $white;
    $btn-focus-bc: transparent;
    $btn-focus-txt: $blue-state;
    $btn-hover-bg: $white;
    $btn-hover-bc: transparent;
    $btn-hover-txt: $blue-state;
    $btn-active-bg: $blue-state;
    $btn-active-bc: transparent;
    $btn-active-txt: $white;
    $btn-disabled-bg: $gray;
    $btn-disabled-bc: transparent;
    $btn-disabled-txt: $grayscale-30;

    border-radius: 0;
    cursor: pointer;
    font-family: inherit;
    margin: 0;
    overflow: hidden;
    position: relative;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
    vertical-align: middle;
    border: 0;
    text-decoration: none;
    text-decoration-thickness: 1px;
    text-underline-offset: 0.3em;
    line-height: 2rem;
    padding: 0 0 0.5rem;

    &__icon {
      --fg-color: currentcolor;

      flex-shrink: 0;
      height: 1.5rem;
      width: 1.5rem;
      margin: 0 0.5rem 0 0;
    }

    &:link,
    &:visited {
      color: $btn-link-txt;
    }

    a.#{$-button-name}:focus,
    &:enabled:focus,
    &.#{$-button-name}--focus {
      background-color: $btn-focus-bg;
      border-color: $btn-focus-bc;
      color: $btn-focus-txt;
      outline: 0;
      text-decoration: underline;
    }

    a.#{$-button-name}:hover,
    &:enabled:hover,
    &.#{$-button-name}--hover {
      background-color: $btn-hover-bg;
      border-color: $btn-hover-bc;
      color: $btn-hover-txt;
      outline: 0;
      text-decoration: underline;
    }

    a.#{$-button-name}:active,
    &:enabled:active,
    &.#{$-button-name}--active,
    &.#{$-button-name}--active:hover {
      $btn-link-txt: $blue-dark;
      $btn-focus-bg: $white;
      $btn-focus-bc: $blue-state;
      $btn-focus-txt: $blue-state;
      $btn-hover-bg: $white;
      $btn-hover-bc: $blue-state;
      $btn-hover-txt: $blue-state;
      $btn-active-bg: #f2f9ff;
      $btn-active-bc: $blue-state;
      $btn-active-txt: $blue-state;
      $btn-disabled-bg: $gray;
      $btn-disabled-bc: $gray;
      $btn-disabled-txt: $grayscale-30;

      background-color: $btn-active-bg;
      border-color: $btn-active-bc;
      color: $btn-active-txt;
      outline: 0;
      text-decoration: none;
    }

    &:disabled {
      background-color: $btn-disabled-bg;
      border-color: $btn-disabled-bc;
      color: $btn-disabled-txt;
      cursor: inherit;
    }
  }
}

.epost-form {
  flex-basis: 100%;
}
</style>
